import {SectionBgView} from '../ui_canvas_engine/SectionBgView'
import {SpriteView} from '../ui_canvas_engine/SpriteView'
import {CardCountView} from '../ui_canvas_engine/CardCountView'
import {UIGameMechanic} from './UIGameEventController'
import {GameStateEnums} from '../../../../shared/GameEnums'
import {UIGameManager} from './UIGameManager'
import {CardDataController} from '@colonist/game-card-types'
import {SectionView} from '../ui_canvas_engine/SectionView'
import {UIGameProgressCardsToolTipView} from './ui_game_tooltip/UIGameProgressCardsToolTipView'
import {CA} from '../analytics/Analytics'
import {FloatingTextDirection} from '../game_ui_view_objects/FloatingText'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import {ArrayUtils} from '@colonist/utils'
import {CardEnum, CardTypeEnum, MechanicDevelopmentCardsState, MechanicProgressCardsState, NestedReadonly} from '@colonist/game-states'
import {Point} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGameBankController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager

    container: SectionBgView

    protected bankIconContainer: SectionView
    protected bankIcon: SpriteView
    protected textColor: string | undefined

    protected cardCountViewContainer: SectionView
    protected resourceCardCountViews: CardCountView[]
    protected developmentCardCountView: CardCountView

    protected developmentCardProgressCardToolTipView: UIGameProgressCardsToolTipView | undefined

    protected cardH: number
    protected cardW: number

    protected isHiddenPermanently: boolean = false
    protected isBeginnerMode: boolean = false

    protected readonly devCardsState: NestedReadonly<MechanicDevelopmentCardsState> | undefined
    protected readonly progressCardsState: NestedReadonly<MechanicProgressCardsState> | undefined

    constructor(uiGameManager: UIGameManager,
                devCardsState: NestedReadonly<MechanicDevelopmentCardsState> | undefined,
                progressCardsState: NestedReadonly<MechanicProgressCardsState> | undefined) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.devCardsState = devCardsState
        this.progressCardsState = progressCardsState

        this.createBankView()
    }

    private createBankView() {
        this.initBankContainer()
        this.initBankIcon()

        this.initializeCardDimension()
        this.initializeCardContainer()
        this.resourceCardCountViews = []
        this.initResourceCardCountViews()

        const maxResourceCardsInView = 5
        this.showFirstXCards(maxResourceCardsInView)

        this.initializeDevelopmentCardContainer()

        this.update()
        if(this.isHiddenPermanently) this.container.visible = false
    }

    protected abstract initializeCardDimension()
    protected abstract initBankContainer()
    protected abstract initBankIcon()
    protected abstract initializeCardContainer()
    protected abstract initializeDevelopmentCardContainer(isProgressCard?: boolean)
    abstract initBankControllerToContainCommodities(): void

    private initResourceCardCountViews() {
        const activeResourceCards = CardDataController.getBaseCardDataForType(this.uiGameManager.gameState.gameModeData.activeResourceCardTypes)
        let cardX = 0

        const isTradeView = false
        const hideBankCards = this.uiGameManager.gameController.gameSettings.hideBankCards
        for(const activeResourceCard of activeResourceCards) {
            const newCardCountView = new CardCountView(this.uiGameManager, cardX, 0, this.cardW, this.cardH, this.cardCountViewContainer, activeResourceCard.cardEnum, this.textColor, isTradeView, hideBankCards)
            this.resourceCardCountViews.push(newCardCountView)

            this.setClickActionToBankCard(newCardCountView)
            cardX += this.cardW
        }
    }

    private setClickActionToBankCard(newCardCountView: CardCountView): void {
        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        if(this.isBeginnerMode) return
        newCardCountView.setClickAction(() => {
            if(this.uiGameManager.tradeController == undefined) return

            const wantedResources = [newCardCountView.cardEnum]
            const isBankClick = true
            this.uiGameManager.tradeController.onClickTrade(newCardCountView, [], wantedResources, isBankClick, FloatingTextDirection.Down)

            if(!UIUtilsEjsVariables.isUserOnMobileApp) return
            CA.sendEventSummed(CA.Category.Game, CA.Label.ClickedGameCardForTrade, UIGameEventEnums.GameEnd)
        })
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.BankStateUpdated: this.update(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            default: break
        }
    }

    private onCanvasResize() {
        this.container.destroy()
        this.createBankView()

        const showCommodities = this.progressCardsState !== undefined
        if(showCommodities) this.initBankControllerToContainCommodities()
    }

    private onBeginnerModeStart() {
        this.isBeginnerMode = true

        this.container.destroy()
        this.createBankView()
    }

    private onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        const isBankHidden = data.hiddenUIElements.includes(HiddenUIElement.Bank)
        this.container.visible = !isBankHidden
        this.isHiddenPermanently = isBankHidden
    }

    protected update() {
        const bankState = this.uiGameManager.gameController.bankState
        const developmentCardCount = this.devCardsState?.bankDevelopmentCards.cards.length ?? this.progressCardsState?.bankProgressCards.cards.length ?? 0
        this.developmentCardCountView.update(developmentCardCount)

        const isBaseGameDevCard = this.progressCardsState === undefined
        if(isBaseGameDevCard) this.developmentCardCountView.setTopCardToolTip(developmentCardCount)

        for(const cardCountViews of this.resourceCardCountViews) {
            const amountOfCards = ArrayUtils.countOccurenceOfValueInArray(bankState.resourceCards.cards, cardCountViews.cardEnum)
            cardCountViews.update(amountOfCards)
            cardCountViews.setTopCardToolTip(amountOfCards)
        }
    }

    getPointFromBank(cardEnum: CardEnum): Point {
        for(const cardCountView of this.resourceCardCountViews) {
            if(cardCountView.cardEnum != cardEnum) continue
            const isCardViewVisible = cardCountView.parent != undefined
            if(!isCardViewVisible) return this.bankIcon.getGlobalPosition()
            return cardCountView.topCard.getGlobalPosition()
        }

        const cardData = CardDataController.getCardDataForCard(cardEnum)
        if(cardData == undefined) return this.bankIcon.getGlobalPosition()

        switch(cardData.cardTypeEnum) {
            case CardTypeEnum.ProgressPolitics:
            case CardTypeEnum.ProgressScience:
            case CardTypeEnum.ProgressTrade:
            case CardTypeEnum.Development: return this.developmentCardCountView.topCard.getGlobalPosition()
            case CardTypeEnum.AnyResource: break
            case CardTypeEnum.Resource: break
            case CardTypeEnum.Commodity: break
        }

        return this.bankIcon.getGlobalPosition()
    }

    protected showFirstXCards(amountOfCardsToShow: number) {
        for(const [index, cardCountView] of this.resourceCardCountViews.entries()) {
            cardCountView.visible = index < amountOfCardsToShow
        }
    }
}
